import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import {
  getBrowserLanguages,
  subscribeBrowserLanguage,
  getStoredLanguage,
  setStoredLanguage,
  subscribeLanguageSession
} from '../../../application/session/browserLanguageService';
import {
  fetchUserPreferences,
  updateUserPreferencesByUserId
} from '../../../application/userPreferences/userPreferencesService';
import { readViewerSessionSnapshot } from '../../auth/presentation/useViewerSession';
import { i18n, loadLanguage } from '../i18n';
import {
  languageDirection,
  resolveLanguage,
  resolvePreferredLanguage
} from '../languageRegistry';
import { resolveLanguageData } from './languageContextConfig';

const getViewerId = () => String(readViewerSessionSnapshot().viewerId || '');
const getBrowserSnapshot = () => getBrowserLanguages().join(',');
export const useLanguagePresentation = () => {
  const browserLanguages = useSyncExternalStore(
    subscribeBrowserLanguage,
    getBrowserSnapshot,
    () => 'en'
  );
  const browserLanguage = resolvePreferredLanguage(browserLanguages.split(','));
  const userId = useSyncExternalStore(
    subscribeLanguageSession,
    getViewerId,
    () => ''
  );
  const [lang, setLang] = useState('en');
  // Empty preference follows this browser; lang is always the effective catalog.
  const [preference, setPreference] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const sequence = useRef(0);
  const invalidateRequests = useCallback(() => {
    ++sequence.current;
  }, []);
  const saveInFlight = useRef(false);
  const apply = useCallback(async (value: string, token: number) => {
    const code = resolveLanguage(value);
    await loadLanguage(code);
    if (sequence.current !== token) return;
    await i18n.changeLanguage(code);
    setLang(code);
    document.documentElement.lang = code;
    document.documentElement.dir = languageDirection(code);
  }, []);

  useEffect(() => {
    const token = ++sequence.current;
    setLoading(true);
    setSaving(false);
    saveInFlight.current = false;
    setError('');
    const cachedPreference = getStoredLanguage(userId);
    setPreference(cachedPreference ? resolveLanguage(cachedPreference) : '');
    const fallback = resolveLanguage(cachedPreference || browserLanguage);
    const load = async () => {
      try {
        await apply(fallback, token);
        if (userId) {
          const preferences = await fetchUserPreferences(userId);
          if (token !== sequence.current) return;
          const selected = preferences?.language
            ? resolveLanguage(preferences.language)
            : '';
          await apply(selected || browserLanguage, token);
          if (token === sequence.current) {
            setPreference(selected);
            setStoredLanguage(selected, userId);
          }
        }
      } catch (cause) {
        if (token === sequence.current) {
          console.error('Language preference could not be loaded', cause);
          setError('loadError');
        }
      } finally {
        if (token === sequence.current) setLoading(false);
      }
    };
    void load();
    return invalidateRequests;
  }, [userId, revision, browserLanguage, apply, invalidateRequests]);

  const switchLang = useCallback(
    async (value: string) => {
      if (loading || saveInFlight.current) return false;
      const token = ++sequence.current;
      const selected = value ? resolveLanguage(value) : '';
      const code = selected || browserLanguage;
      saveInFlight.current = true;
      setSaving(true);
      setError('');
      try {
        await loadLanguage(code);
        if (token !== sequence.current) return false;
        if (userId)
          await updateUserPreferencesByUserId(userId, { language: selected });
        if (token !== sequence.current) return false;
        await apply(code, token);
        if (token !== sequence.current) return false;
        setPreference(selected);
        setStoredLanguage(selected, userId);
        return true;
      } catch (cause) {
        if (token === sequence.current) {
          console.error('Language preference could not be saved', cause);
          setError('saveError');
        }
        return false;
      } finally {
        if (token === sequence.current) {
          saveInFlight.current = false;
          setSaving(false);
        }
      }
    },
    [loading, userId, browserLanguage, apply]
  );
  return {
    lang,
    preference,
    switchLang,
    loading,
    saving,
    error,
    userId,
    retry: () => setRevision((value) => value + 1),
    currentLangData: resolveLanguageData(lang)
  };
};
